import { ActionRuleParams } from '../../types';
import { formatValidation, getIslandLetter } from '../island-validation-format';
import { IslandType } from '../../../islands/enums';
import { libLang } from '../../../../lang';
import { NotificationType } from '../../../notifications/enums';

import type { Island } from '../../../islands/island.class';

const INTERNAL_SUPPORT_MAX_DEPTH = 30;

export const INTERNAL_SUPPORTS_OVERRIDE_PREFIX = 'addon.internalSupports.';

export const getInternalSupportsOverrideKey = (islandId: string): string => `${INTERNAL_SUPPORTS_OVERRIDE_PREFIX}${islandId}`;

/** #733's documented trigger (base depth over 30") plus the corpus's second confirmed path: smoker stands regardless of depth. */
export const islandRequiresInternalSupports = (island: Island): boolean => {
	if (island.type === IslandType.Smoker) return true;
	const depth = island.size.depth;
	if (typeof depth === 'number') return depth > INTERNAL_SUPPORT_MAX_DEPTH;
	return (depth?.left ?? 0) > INTERNAL_SUPPORT_MAX_DEPTH || (depth?.right ?? 0) > INTERNAL_SUPPORT_MAX_DEPTH;
};

/** Warns when a triggered island has internal supports explicitly toggled off — the override stays respected, but visibly. */
export const internalSupportsDisabled = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { kitchen } = refMap;
	const { islands } = kitchen.design ?? refMap;
	const { notifications } = kitchen.design;

	notifications.purgeReferences(libLang.validations.island.supports.disabled);
	if (!islands?.children) return;

	islands.children.forEach((island) => {
		if (!islandRequiresInternalSupports(island)) return;
		if (kitchen.blueprintOverrides?.[getInternalSupportsOverrideKey(island.id)] !== '0') return;
		const reason = island.type === IslandType.Smoker
			? 'smoker stands call for internal support panels'
			: `base depth over ${INTERNAL_SUPPORT_MAX_DEPTH}" calls for internal support panels`;
		notifications.add({
			refId: island.id,
			validationCode: libLang.validations.island.supports.disabled,
			description: formatValidation({ letter: getIslandLetter(island), islandDescription: island.description, issue: `Internal support panels are toggled off, but ${reason}` }),
			type: NotificationType.Warn
		});
	});
};
